/**
 * 平面の幾何（mm、y は下向き）。純粋な関数だけ。
 */
import type { Point } from "@el/schema";

export type Rect = { x: number; y: number; w: number; h: number };

const EPS = 1e-7;

/** 符号付きの面積（靴ひも公式）。y 下向きでは時計回りが正 */
export function signedArea(poly: readonly Point[]): number {
  let s = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

/** 符号なしの面積（mm²） */
export function polygonArea(poly: readonly Point[]): number {
  if (poly.length < 3) return 0;
  return Math.abs(signedArea(poly));
}

function onSegment(p: Point, a: Point, b: Point): boolean {
  const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (Math.abs(cross) > EPS * Math.max(1, len)) return false;
  return p.x >= Math.min(a.x, b.x) - EPS && p.x <= Math.max(a.x, b.x) + EPS && p.y >= Math.min(a.y, b.y) - EPS && p.y <= Math.max(a.y, b.y) + EPS;
}

/** 辺の上は内側 */
export function pointInPolygon(p: Point, poly: readonly Point[]): boolean {
  const n = poly.length;
  if (n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (onSegment(p, a, b)) return true;
    if (a.y > p.y !== b.y > p.y) {
      const x = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
      if (p.x < x) inside = !inside;
    }
  }
  return inside;
}

/** 線分が矩形の開いた内部を通るか（辺に沿う・接するだけは通らない） */
export function segmentCrossesRectInterior(a: Point, b: Point, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const p = [-dx, dx, -dy, dy];
  const q = [a.x - r.x, r.x + r.w - a.x, a.y - r.y, r.y + r.h - a.y];
  for (let i = 0; i < 4; i++) {
    const pi = p[i]!;
    const qi = q[i]!;
    if (pi === 0) {
      if (qi < 0) return false;
    } else {
      const t = qi / pi;
      if (pi < 0) {
        if (t > t1) return false;
        if (t > t0) t0 = t;
      } else {
        if (t < t0) return false;
        if (t < t1) t1 = t;
      }
    }
  }
  if (t1 - t0 <= 0) return false;
  const tm = (t0 + t1) / 2;
  const mx = a.x + dx * tm;
  const my = a.y + dy * tm;
  return mx > r.x + EPS && mx < r.x + r.w - EPS && my > r.y + EPS && my < r.y + r.h - EPS;
}

function rectCorners(r: Rect): Point[] {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
}

/** 4隅が内側（辺の上を含む）かつ多角形の辺が矩形の内部を通らない */
export function rectInsidePolygon(r: Rect, poly: readonly Point[]): boolean {
  if (poly.length < 3) return false;
  for (const c of rectCorners(r)) if (!pointInPolygon(c, poly)) return false;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    if (segmentCrossesRectInterior(poly[i]!, poly[(i + 1) % n]!, r)) return false;
  }
  return true;
}

/** 接するだけは重ならない。marginMm を指定すると、すき間が margin 未満なら重なりとみなす */
export function rectsOverlap(a: Rect, b: Rect, marginMm = 0): boolean {
  return a.x < b.x + b.w + marginMm && b.x < a.x + a.w + marginMm && a.y < b.y + b.h + marginMm && b.y < a.y + a.h + marginMm;
}

/** 矩形と多角形の内部どうしが重なるか（辺が接するだけは重ならない） */
export function rectPolygonOverlap(r: Rect, poly: readonly Point[]): boolean {
  const n = poly.length;
  if (n < 3 || r.w <= 0 || r.h <= 0) return false;
  for (let i = 0; i < n; i++) {
    if (segmentCrossesRectInterior(poly[i]!, poly[(i + 1) % n]!, r)) return true;
  }
  // 多角形が矩形を包む
  return pointInPolygon({ x: r.x + r.w / 2, y: r.y + r.h / 2 }, poly);
}

/** 和集合の面積（走査線。重なりを二重に数えない） */
export function unionArea(rects: readonly Rect[]): number {
  const rs = rects.filter((r) => r.w > 0 && r.h > 0);
  if (rs.length === 0) return 0;
  const xs = Array.from(new Set(rs.flatMap((r) => [r.x, r.x + r.w]))).sort((a, b) => a - b);
  const byX = [...rs].sort((a, b) => a.x - b.x);
  let area = 0;
  const intervals: [number, number][] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    const x0 = xs[i]!;
    const x1 = xs[i + 1]!;
    intervals.length = 0;
    for (const r of byX) {
      if (r.x > x0) break;
      if (r.x + r.w >= x1) intervals.push([r.y, r.y + r.h]);
    }
    if (intervals.length === 0) continue;
    intervals.sort((a, b) => a[0] - b[0]);
    let covered = 0;
    let [s, e] = intervals[0]!;
    for (let k = 1; k < intervals.length; k++) {
      const [a, b] = intervals[k]!;
      if (a > e) {
        covered += e - s;
        s = a;
        e = b;
      } else if (b > e) e = b;
    }
    covered += e - s;
    area += covered * (x1 - x0);
  }
  return area;
}

export function boundingBox(points: readonly Point[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function rectToPolygon(r: Rect): Point[] {
  return rectCorners(r);
}

export function rectUnion(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/** 2 本の線分が交わるか（端点で接する・重なるを含む） */
export function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  const o = (p: Point, q: Point, r: Point) => {
    const v = (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
    return Math.abs(v) < EPS ? 0 : v > 0 ? 1 : -1;
  };
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(c, a, b)) return true;
  if (o2 === 0 && onSegment(d, a, b)) return true;
  if (o3 === 0 && onSegment(a, c, d)) return true;
  if (o4 === 0 && onSegment(b, c, d)) return true;
  return false;
}

/** 隣り合わない辺どうしが交わる多角形（自己交差）か */
export function isSelfIntersecting(poly: readonly Point[]): boolean {
  const n = poly.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    for (let j = i + 1; j < n; j++) {
      if (j === i || (j + 1) % n === i || j === (i + 1) % n) continue;
      if (segmentsIntersect(a, b, poly[j]!, poly[(j + 1) % n]!)) return true;
    }
  }
  return false;
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
